import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { navigate } from 'gatsby';
import { useForm } from 'react-hook-form';
import { isBrowser } from '@helpers/utils';

import Query from '@queries/academy/approvals';

import Form from 'react-bootstrap/Form';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import BackgroundImage from 'gatsby-background-image';
import { convertToBgImage } from 'gbimage-bridge';

import SEO from '@common/SEO';
import Main from '@common/Main';
import Container from '@common/Container';
import Section from '@common/Section';
import Row from '@common/Row';
import Heading from '@common/Heading';
import Img from '@common/Image';
import Link from '@common/Link';
import Button from '@common/Button';
import RichText from '@common/RichText';

import * as styles from '@styles/modules/academy.module.scss';

const Approvals = () => {
  const data = Query();
  const {
    title,
    subtitle,
    description,
    image,
    primaryButton,
    searchTitle,
    searchButton,
    accreditations
  } = data?.contentfulAcademyApprovalPage;
  const bgImage = convertToBgImage(image.gatsbyImageData);
  const allStates = data.allContentfulAcademyStateApproval.nodes;
  const [states, setStates] = useState(allStates);
  const [countries, setCountries] = useState(null);
  const { register, watch, formState: { isDirty }, handleSubmit } = useForm();
  const scrollToTop = () => isBrowser() && window.scrollTo(0,0);
  const onSubmit = (data) => {
    // Stop form from submitting
    event.preventDefault();
    event.stopPropagation();

    // Navigate to state page
    const state = states.find(x => x.id === data.state);
    if (state?.slug) {
      navigate(`/academy/approvals${state.slug}`)
    }
  };
  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (name === 'country' && type === 'change') {
        const newStates = allStates.filter(x => x.country === value.country)
        setStates(newStates);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);
  useEffect(() => {
    const newCountries = [...new Set(allStates.map(approval => approval.country))];
    setCountries(newCountries);
    setTimeout(() => scrollToTop(), 100);
  },[]);
  return (
    <Main>
      <Section spacing='mt-4 mb-lg-5'>
        <Container>
          <Row className='flex-column flex-lg-row'>
            <div className='col col-lg-5 col-xl-4 mb-3 me-lg-3'>
              <Form className={clsx('bg-light-grey-2 rounded', styles.stateForm)} onSubmit={handleSubmit(onSubmit)}>
                <Heading type='h2' className='fs-24 mb-3'>{searchTitle}</Heading>
                {(countries && countries.length > 1) &&
                  <Form.Group controlId='country' className='mb-3'>
                    <FloatingLabel controlId='country' label='Select your country'>
                      <Form.Select
                        name='country'
                        className='form-control'
                        {...register('country')}
                      >
                        <option value={null}>{'Country'}</option>
                        {countries && countries.map(country => (
                          <option key={country} value={country}>{country}</option>
                        ))}
                      </Form.Select>
                    </FloatingLabel>
                  </Form.Group>
                }
                <Form.Group controlId='state' className='mb-3 '>
                  <FloatingLabel controlId='state' label='Select your state/province'>
                    <Form.Select
                      name='state'
                      className='form-control'
                      {...register('state', { required: true })}
                    >
                      <option>{'State/Province'}</option>
                      {states && states.map(item => (
                        <option key={item.id} value={item.id}>{item.state}</option>
                      ))}
                    </Form.Select>
                  </FloatingLabel>
                </Form.Group>
                {searchButton &&
                  <Button
                    variant={searchButton.variant}
                    to={searchButton.link}
                    copy={searchButton.copy}
                    size={searchButton.size}
                    addClass={clsx('w-100', searchButton.addClass)}
                    disabled={!isDirty}
                  />
                }
              </Form>
            </div>
            <div className='d-flex flex-column col'>
              {description && 
                <div className='order-lg-1 my-3'>
                  <RichText json={description}/>
                </div>
              }
              {accreditations.list &&
                <div className='d-flex flex-row order-lg-2 my-3 align-items-center'>
                  {accreditations.list.map(item => (
                    <Link key={item.id} to={item.link}>
                      <Img image={item.image.gatsbyImageData} file={item.image.file} />
                    </Link>
                  ))}
                </div>
              }
              <BackgroundImage className='my-3 mt-lg-0 rounded overflow-hidden order-lg-0' {...bgImage}>
                <div className={clsx('p-4', styles.catalogImage)}>
                  <div className={clsx('d-flex flex-column align-items-start', styles.catalogHeading)}>
                    <Heading type='h1' className='h3 mb-3 col-lg-10'>{title}</Heading>
                    <p className='fs-16 d-lg-none'>{subtitle}</p>
                    {primaryButton &&
                      <div className='d-flex justify-content-center'>
                        <Button
                          variant={primaryButton.variant}
                          to={primaryButton.link}
                          copy={primaryButton.copy}
                          size={primaryButton.size}
                          addClass={primaryButton.addClass}
                        />
                      </div>
                    }
                  </div>
                </div>
              </BackgroundImage>
            </div>
          </Row>
        </Container>
      </Section>
    </Main>
  )
}

export default Approvals

export const Head = ({ location }) => {
  return (
    <SEO
      index={true}
      titleTag={'Approvals'}
      location={location}
    />
  )
}
